import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { useAppSelector, useAppDispatch } from '@store';
import {
  ajvResolver,
  getResponseErrors,
  ResponseError,
  responseErrorsResolver,
  SelectItemProps,
  ValidatorsProps,
} from '@ui/src';
import {
  useEditPSPDetailsMutation,
  useLazyGetPSPDetailsQuery,
} from '../../../settings/store/services/details';
import {
  EditPSPLanguagesFormValues,
  PSPLanguangesKeys,
} from '../../../settings/types';
import { setIsDirty } from '../../../settings/store/slices/details';
import languagesList from '../helpers/languages';

const useLanguages = () => {
  const [defaultFormValues, setDefaultFormValues] =
    useState<EditPSPLanguagesFormValues>();
  const [globalError, setGlobalError] = useState('');
  const [errorList, setErrorList] = useState('');
  const [editPanelIsOpen, setEditPanelIsOpen] = useState(false);
  const [editPanelType, setEditPanelType] = useState('');
  const [editKey, setEditKey] = useState<
    keyof EditPSPLanguagesFormValues | undefined
  >(undefined);
  const [notEditKey, setNotEditKey] = useState<
    keyof EditPSPLanguagesFormValues | undefined
  >(undefined);
  const [filteredLanguagesList, setFilteredLanguagesList] = useState<
    SelectItemProps[] | undefined
  >(undefined);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [allAvailableLanguages, setAllAvailableLanguages] = useState<
    SelectItemProps[] | undefined
  >(undefined);
  const [searchText, setSearchText] = useState('');

  const {
    settings: {
      details: { validators, pspDetails },
    },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [edit, { isLoading: editIsLoading }] = useEditPSPDetailsMutation();
  const [getDetails, { isLoading }] = useLazyGetPSPDetailsQuery();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    setValue,
    formState: { errors },
    clearErrors,
  } = useForm<EditPSPLanguagesFormValues>({
    resolver: ajvResolver(validators?.updateSettingSchema as ValidatorsProps),
  });

  const fields = watch();

  const isDirty = !isEqual(fields, defaultFormValues);

  const getDetailsAction = useCallback(async () => {
    const response = (await getDetails()) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({ error: response?.error, setErrorList });
    }
  }, []);

  const onSearchLanguages = (search: string) => {
    setSearchText(search);

    if (notEditKey) {
      const selectedNotEditLanguagesList = fields[notEditKey];

      if (selectedNotEditLanguagesList) {
        const nextFilteredLanguagesList = languagesList?.filter((item) =>
          selectedNotEditLanguagesList?.every(
            (selectedItem) => item.value !== selectedItem,
          ),
        );

        if (search) {
          const list = nextFilteredLanguagesList?.filter(
            (item) =>
              item.name.toLowerCase().includes(search.toLowerCase()) ||
              item.suffix.toLowerCase().includes(search.toLowerCase()),
          );

          setFilteredLanguagesList(list);
        } else {
          setFilteredLanguagesList(nextFilteredLanguagesList);
        }
      }
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const updatedSettings: {
      [key: string]:
        | string
        | string[]
        | boolean
        | number
        | Record<string, string>
        | Record<string, boolean>;
      key: PSPLanguangesKeys;
    }[] = [];

    if (
      !isEqual(
        values.applicationsAvailableLanguages,
        defaultFormValues?.applicationsAvailableLanguages,
      )
    ) {
      updatedSettings.push({
        applicationsAvailableLanguages: values.applicationsAvailableLanguages,
        key: PSPLanguangesKeys.APPLICATIONS_AVAILABLE_LANGUAGES,
      });
    }

    if (
      !isEqual(
        values.applicationsNotAvailableLanguages,
        defaultFormValues?.applicationsNotAvailableLanguages,
      )
    ) {
      updatedSettings.push({
        applicationsNotAvailableLanguages:
          values.applicationsNotAvailableLanguages,
        key: PSPLanguangesKeys.APPLICATIONS_NOT_AVAILABLE_LANGUAGES,
      });
    }

    if (
      !isEqual(
        values.documentsAvailableLanguages,
        defaultFormValues?.documentsAvailableLanguages,
      )
    ) {
      updatedSettings.push({
        documentsAvailableLanguages: values.documentsAvailableLanguages,
        key: PSPLanguangesKeys.DOCUMENTS_AVAILABLE_LANGUAGES,
      });
    }

    if (
      !isEqual(
        values.documentsNotAvailableLanguages,
        defaultFormValues?.documentsNotAvailableLanguages,
      )
    ) {
      updatedSettings.push({
        documentsNotAvailableLanguages: values.documentsNotAvailableLanguages,
        key: PSPLanguangesKeys.DOCUMENTS_NOT_AVAILABLE_LANGUAGES,
      });
    }

    if (
      !isEqual(
        values.transactionsAvailableLanguages,
        defaultFormValues?.transactionsAvailableLanguages,
      )
    ) {
      updatedSettings.push({
        transactionsAvailableLanguages: values.transactionsAvailableLanguages,
        key: PSPLanguangesKeys.TRANSACTIONS_AVAILABLE_LANGUAGES,
      });
    }

    if (
      !isEqual(
        values.transactionsNotAvailableLanguages,
        defaultFormValues?.transactionsNotAvailableLanguages,
      )
    ) {
      updatedSettings.push({
        transactionsNotAvailableLanguages:
          values.transactionsNotAvailableLanguages,
        key: PSPLanguangesKeys.TRANSACTIONS_NOT_AVAILABLE_LANGUAGES,
      });
    }

    if (updatedSettings.length > 0) {
      const response = (await Promise.all(
        updatedSettings.map(async (item) => {
          const res = await edit(item);

          return res;
        }),
      )) as { error?: ResponseError }[];

      if (response.find((item) => item.error)) {
        const errorsResponse = response.filter((item) => item.error);

        errorsResponse.forEach((item) => {
          responseErrorsResolver({
            errors: item.error as ResponseError,
            setError,
            setGlobalError,
          });
        });
      } else {
        dispatch(setIsDirty(false));

        setDefaultFormValues({
          applicationsAvailableLanguages:
            values?.applicationsAvailableLanguages || [],
          applicationsNotAvailableLanguages:
            values?.applicationsNotAvailableLanguages || [],
          documentsAvailableLanguages:
            values?.documentsAvailableLanguages || [],
          documentsNotAvailableLanguages:
            values?.documentsNotAvailableLanguages || [],
          transactionsAvailableLanguages:
            values?.transactionsAvailableLanguages || [],
          transactionsNotAvailableLanguages:
            values?.transactionsNotAvailableLanguages || [],
        });
      }
    }
  });

  const handleClose = () => {
    if (
      editKey &&
      defaultFormValues &&
      !isEqual(fields[editKey], defaultFormValues[editKey])
    ) {
      setValue(editKey, defaultFormValues[editKey]);
    }

    if (editKey) {
      clearErrors([editKey]);
    }

    setEditPanelIsOpen(false);
    setEditPanelType('');
    setNotEditKey(undefined);
    setDirtyPopupVisible(false);
    setAllAvailableLanguages(undefined);
    setFilteredLanguagesList(undefined);
    setSearchText('');
    setGlobalError('');

    setTimeout(() => {
      setEditKey(undefined);
    }, 100);
  };

  useEffect(() => {
    if (!pspDetails) {
      getDetailsAction();
    }
  }, [pspDetails]);

  useEffect(() => {
    if (pspDetails) {
      const applicationsAvailableLanguages = pspDetails?.find(
        (item) => item.key === 'APPLICATIONS-AVAILABLE-LANGUAGES',
      );
      const applicationsNotAvailableLanguages = pspDetails?.find(
        (item) => item.key === 'APPLICATIONS-NOT-AVAILABLE-LANGUAGES',
      );
      const documentsAvailableLanguages = pspDetails?.find(
        (item) => item.key === 'DOCUMENTS-AVAILABLE-LANGUAGES',
      );
      const documentsNotAvailableLanguages = pspDetails?.find(
        (item) => item.key === 'DOCUMENTS-NOT-AVAILABLE-LANGUAGES',
      );
      const transactionsAvailableLanguages = pspDetails?.find(
        (item) => item.key === 'TRANSACTIONS-AVAILABLE-LANGUAGES',
      );
      const transactionsNotAvailableLanguages = pspDetails?.find(
        (item) => item.key === 'TRANSACTIONS-NOT-AVAILABLE-LANGUAGES',
      );

      reset({
        applicationsAvailableLanguages:
          (applicationsAvailableLanguages?.data?.value as string[]) || [],
        applicationsNotAvailableLanguages:
          (applicationsNotAvailableLanguages?.data?.value as string[]) || [],
        documentsAvailableLanguages:
          (documentsAvailableLanguages?.data?.value as string[]) || [],
        documentsNotAvailableLanguages:
          (documentsNotAvailableLanguages?.data?.value as string[]) || [],
        transactionsAvailableLanguages:
          (transactionsAvailableLanguages?.data?.value as string[]) || [],
        transactionsNotAvailableLanguages:
          (transactionsNotAvailableLanguages?.data?.value as string[]) || [],
      });

      setDefaultFormValues({
        applicationsAvailableLanguages:
          (applicationsAvailableLanguages?.data?.value as string[]) || [],
        applicationsNotAvailableLanguages:
          (applicationsNotAvailableLanguages?.data?.value as string[]) || [],
        documentsAvailableLanguages:
          (documentsAvailableLanguages?.data?.value as string[]) || [],
        documentsNotAvailableLanguages:
          (documentsNotAvailableLanguages?.data?.value as string[]) || [],
        transactionsAvailableLanguages:
          (transactionsAvailableLanguages?.data?.value as string[]) || [],
        transactionsNotAvailableLanguages:
          (transactionsNotAvailableLanguages?.data?.value as string[]) || [],
      });
    }
  }, [pspDetails]);

  useEffect(() => {
    dispatch(setIsDirty(isDirty));
  }, [isDirty]);

  useEffect(() => {
    if (editPanelIsOpen && notEditKey && !filteredLanguagesList) {
      const selectedNotEditLanguagesList = fields[notEditKey];

      if (selectedNotEditLanguagesList) {
        const nextFilteredLanguagesList = languagesList?.filter((item) =>
          selectedNotEditLanguagesList?.every(
            (selectedItem) => item.value !== selectedItem,
          ),
        );

        setFilteredLanguagesList(nextFilteredLanguagesList);
        setAllAvailableLanguages(nextFilteredLanguagesList);
      }
    }
  }, [editPanelIsOpen, notEditKey, fields, filteredLanguagesList]);

  return {
    isLoading,
    errorList,
    control,
    onSubmit,
    globalError,
    editIsLoading,
    isDirty,
    editPanelIsOpen,
    setEditPanelIsOpen,
    editPanelType,
    setEditPanelType,
    editKey,
    setEditKey,
    handleClose,
    setNotEditKey,
    setValue,
    filteredLanguagesList,
    onSearchLanguages,
    dirtyPopupVisible,
    setDirtyPopupVisible,
    allAvailableLanguages,
    fields,
    searchText,
    errors,
  };
};

export default useLanguages;
